import React, { useRef, useState } from "react";
import {
  Avatar, Box, Flex, Text, Icon, IconButton, Badge,
  Spinner, useColorModeValue, useToast,
} from "@chakra-ui/react";
import { MdCameraAlt } from "react-icons/md";
import Card from "components/card/Card.js";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { updateProfile } from "firebase/auth";
import { doc, updateDoc } from "firebase/firestore";
import { storage, auth, db } from "../../../../../../firebase";

const ROLE_LABELS = {
  admin:      { label: 'Administrador', color: 'purple' },
  supervisor: { label: 'Supervisor',    color: 'orange' },
  tecnico:    { label: 'Técnico',       color: 'blue'   },
  cliente:    { label: 'Cliente',       color: 'teal'   },
};

export default function Profile({ formData, uid, onPhotoUpdate }) {
  const fileRef   = useRef(null);
  const toast     = useToast();
  const [uploading, setUploading] = useState(false);

  const textColor = useColorModeValue("navy.700", "white");
  const subColor  = useColorModeValue("gray.500", "gray.400");
  const overlayBg = "blackAlpha.600";

  const firstName = formData?.firstName || formData?.first_name || '';
  const lastName  = formData?.lastName  || formData?.last_name  || '';
  const fullName  = [firstName, lastName].filter(Boolean).join(' ') || 'Usuário';
  const email     = formData?.email     || '';
  const cargo     = formData?.cargo     || '';
  const role      = formData?.role      || '';
  const photo     = formData?.photoURL  || undefined;
  const badge     = ROLE_LABELS[role];

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uid) return;

    // Aceita apenas imagens até 5 MB
    if (!file.type.startsWith("image/")) {
      toast({ title: "Arquivo inválido", description: "Selecione uma imagem.", status: "warning", duration: 3000, position: "top-right" });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "Imagem muito grande", description: "Máximo 5 MB.", status: "warning", duration: 3000, position: "top-right" });
      return;
    }

    setUploading(true);
    try {
      const fileRef = storageRef(storage, `avatars/${uid}`);
      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);

      // Atualiza Firebase Auth + Firestore
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, { photoURL: url });
      }
      await updateDoc(doc(db, "users", uid), { photoURL: url });

      onPhotoUpdate?.(url);
      toast({ title: "Foto atualizada!", status: "success", duration: 3000, position: "top-right" });
    } catch (err) {
      console.error(err);
      toast({ title: "Erro ao enviar foto", description: "Tente novamente.", status: "error", duration: 4000, position: "top-right" });
    } finally {
      setUploading(false);
      // limpa o input para permitir re-upload do mesmo arquivo
      e.target.value = "";
    }
  };

  return (
    <Card>
      <Flex direction="column" align="center" py="10px" gap="12px">
        {/* Avatar com overlay de câmera */}
        <Box position="relative" cursor="pointer" onClick={() => !uploading && fileRef.current?.click()}>
          <Avatar
            src={photo}
            name={fullName}
            w={{ base: "90px", md: "100px" }}
            h={{ base: "90px", md: "100px" }}
            border="3px solid"
            borderColor="brand.500"
          />
          {/* Overlay câmera */}
          <Flex
            position="absolute" bottom="0" left="0" right="0"
            h="40%" borderBottomRadius="full"
            bg={overlayBg} align="center" justify="center"
            opacity={uploading ? 1 : 0}
            _hover={{ opacity: 1 }}
            transition="opacity 0.2s"
          >
            {uploading
              ? <Spinner size="sm" color="white" />
              : <Icon as={MdCameraAlt} color="white" boxSize="16px" />
            }
          </Flex>
        </Box>

        {/* Input oculto */}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />

        {/* Nome e info */}
        <Box textAlign="center">
          <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="800" color={textColor} lineHeight="1.2">
            {fullName}
          </Text>
          {cargo && (
            <Text fontSize="sm" color={subColor} mt="2px">{cargo}</Text>
          )}
          {email && (
            <Text fontSize="xs" color={subColor} mt="2px">{email}</Text>
          )}
        </Box>

        {/* Badge de role */}
        {badge && (
          <Badge colorScheme={badge.color} borderRadius="full" px="12px" py="4px" fontSize="xs">
            {badge.label}
          </Badge>
        )}

        {/* Dica de clique */}
        <Text fontSize="11px" color={subColor} mt="-4px">
          Clique na foto para alterar
        </Text>
      </Flex>
    </Card>
  );
}
